import {
  formatDateRange,
  formatDateTimeWithZone,
} from '../../../../ts-utils/date-utils'
import {
  formatRegistrationDateRange,
  formatRegistrationMonthDay,
  registrationCalendarDaysUntil,
} from '../../../../ts-utils/registration-datetime'
import { CLOSING_SOON_DAYS } from '../constants'
import type { OrgRegistration } from '../types'
import {
  getSpotsSummaryLabel,
  summarizeSpots,
  type SpotsSummary,
} from './availability'

/** The chip on a card: the tab's status, with "closing soon" split out of open. */
export type OrgRegistrationBadge = 'open' | 'closingSoon' | 'upcoming' | 'past'

const BADGE_LABELS: Record<OrgRegistrationBadge, string> = {
  open: 'Open',
  closingSoon: 'Closing Soon',
  upcoming: 'Upcoming',
  past: 'Closed',
}

/** Everything a registration card shows that is derived, not read straight off the data. */
export interface OrgRegistrationCardModel {
  badge: OrgRegistrationBadge
  badgeLabel: string
  /** "Closes In" / "Opens In" / "Closed On". */
  countdownLabel: string
  /** "9 days" / "Today" for open and upcoming; "Jun 30" for past. */
  countdownValue: string
  /** The exact moment, with its zone: "Oct 16, 2026 11:59 PM EDT". */
  countdownDetail: string
  /** Season dates, e.g. "Oct 10, 2026 – Mar 27, 2027"; '' when unset. */
  seasonLabel: string
  /** Registration window dates in the registration's zone. */
  windowLabel: string
  /** "Opened Aug 24" — open registrations only. */
  windowStartLabel: string
  /** "Closes Oct 16" — open registrations only. */
  windowEndLabel: string
  /** Share of the window already elapsed, 0–100 — open registrations only. */
  windowProgress: number | null
  /** One line for an upcoming or past registration; '' for open. */
  note: string
  spots: SpotsSummary
  /** "11 team spots left · 1 full", "Full", or '' when nothing is capped. */
  spotsLabel: string
  /**
   * What a division's availability reads while the registration isn't open —
   * "Opens Jan 15" or "Closed"; '' while open, when live spots are shown.
   */
  inactiveAvailabilityLabel: string
}

/** The countdown's value when the registration has no window set. */
const NO_DATE_LABEL = 'No Date Set'

const daysLabel = (days: number): string => {
  if (days <= 0) {
    return 'Today'
  }
  return `${days} day${days === 1 ? '' : 's'}`
}

/**
 * How far through its window a registration is, 0–100. Pure instant math —
 * the zone does not move an elapsed share.
 */
export const getWindowProgress = (
  opensAt: string | null,
  closesAt: string | null,
  now: Date = new Date()
): number | null => {
  if (!opensAt || !closesAt) {
    return null
  }
  const opens = Date.parse(opensAt)
  const closes = Date.parse(closesAt)
  if (Number.isNaN(opens) || Number.isNaN(closes) || closes <= opens) {
    return null
  }
  const share = (now.getTime() - opens) / (closes - opens)
  return Math.round(Math.min(1, Math.max(0, share)) * 100)
}

/**
 * Derives a card's display model. `now` is a parameter so the countdown is
 * testable and every card on a screen reads the same moment.
 */
export const buildOrgRegistrationCardModel = (
  registration: OrgRegistration,
  now: Date = new Date()
): OrgRegistrationCardModel => {
  const { status, opensAt, closesAt, timezone, divisions } = registration
  const spots = summarizeSpots(divisions)
  const windowLabel = formatRegistrationDateRange(opensAt, closesAt, timezone)
  const shared = {
    seasonLabel: formatDateRange(
      registration.seasonStartsAt,
      registration.seasonEndsAt
    ),
    windowLabel,
    spots,
    spotsLabel: getSpotsSummaryLabel(spots),
  }

  if (status === 'upcoming') {
    return {
      ...shared,
      badge: 'upcoming',
      badgeLabel: BADGE_LABELS.upcoming,
      countdownLabel: 'Opens In',
      countdownValue: daysLabel(
        registrationCalendarDaysUntil(opensAt, timezone, now) ?? 0
      ),
      countdownDetail: formatDateTimeWithZone(opensAt, timezone),
      windowStartLabel: '',
      windowEndLabel: '',
      windowProgress: null,
      note: windowLabel ? `Registration runs ${windowLabel}.` : '',
      inactiveAvailabilityLabel: `Opens ${formatRegistrationMonthDay(
        opensAt,
        timezone
      )}`,
    }
  }

  if (status === 'past') {
    return {
      ...shared,
      badge: 'past',
      badgeLabel: BADGE_LABELS.past,
      countdownLabel: 'Closed On',
      countdownValue: formatRegistrationMonthDay(closesAt, timezone),
      countdownDetail: formatDateTimeWithZone(closesAt, timezone),
      windowStartLabel: '',
      windowEndLabel: '',
      windowProgress: null,
      note: windowLabel ? `Registration ran ${windowLabel}.` : '',
      inactiveAvailabilityLabel: 'Closed',
    }
  }

  // Open with no window set: nothing to count down to or measure against.
  if (!closesAt) {
    return {
      ...shared,
      badge: 'open',
      badgeLabel: BADGE_LABELS.open,
      countdownLabel: 'Closes',
      countdownValue: NO_DATE_LABEL,
      countdownDetail: '',
      windowStartLabel: '',
      windowEndLabel: '',
      windowProgress: null,
      note: '',
      inactiveAvailabilityLabel: '',
    }
  }

  const daysLeft = registrationCalendarDaysUntil(closesAt, timezone, now) ?? 0
  const badge: OrgRegistrationBadge =
    daysLeft <= CLOSING_SOON_DAYS ? 'closingSoon' : 'open'
  return {
    ...shared,
    badge,
    badgeLabel: BADGE_LABELS[badge],
    countdownLabel: 'Closes In',
    countdownValue: daysLabel(daysLeft),
    countdownDetail: formatDateTimeWithZone(closesAt, timezone),
    windowStartLabel: opensAt
      ? `Opened ${formatRegistrationMonthDay(opensAt, timezone)}`
      : '',
    windowEndLabel: `Closes ${formatRegistrationMonthDay(closesAt, timezone)}`,
    windowProgress: getWindowProgress(opensAt, closesAt, now),
    note: '',
    inactiveAvailabilityLabel: '',
  }
}
